import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, FormControl, InputLabel,
  MenuItem, Paper, Select, Stack, TextField, Typography,
} from '@mui/material';
import { adminApi } from '../../../api/admin';

const statusLabels = {
  PENDING: '접수',
  REVIEWING: '검토 중',
  ACTIONED: '조치 완료',
  DISMISSED: '반려',
};

const typeLabels = {
  USER: '회원',
  ALBUM_REVIEW: '앨범 리뷰',
  COMMENT: '댓글',
  PLAYLIST: '플레이리스트',
};

const reasonLabels = {
  SPAM: '스팸·홍보',
  HARASSMENT: '괴롭힘·위협',
  HATE_OR_ABUSE: '혐오·모욕',
  SEXUAL_OR_INAPPROPRIATE: '성적·부적절',
  MISINFORMATION: '거짓·오해 유발',
  COPYRIGHT: '저작권·권리 침해',
  OTHER: '기타',
};

function ReportCard({ report, onSaved }) {
  const [status, setStatus] = useState(report.status);
  const [adminNote, setAdminNote] = useState(report.adminNote || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const save = async () => {
    setBusy(true);
    setError('');
    try {
      await adminApi.updateSafetyReport(report.id, { status, adminNote });
      onSaved();
    } catch (e) {
      setError(e.message || '신고 상태를 저장하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={1.5}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
          <Chip size="small" label={statusLabels[report.status] || report.status} />
          <Typography fontWeight={700}>{typeLabels[report.contentType] || report.contentType} #{report.contentId}</Typography>
          <Typography color="text.secondary" variant="body2">{reasonLabels[report.reason] || report.reason}</Typography>
          <Typography color="text.secondary" sx={{ ml: { sm: 'auto' } }} variant="caption">{report.createdAt}</Typography>
        </Stack>
        <Typography variant="body2">신고자: {report.reporterNickname} (#{report.reporterId}) · 대상: {report.reportedUserNickname} (#{report.reportedUserId})</Typography>
        {report.details ? <Typography sx={{ whiteSpace: 'pre-wrap' }}>{report.details}</Typography> : null}
        {error ? <Alert severity="error">{error}</Alert> : null}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id={`safety-status-${report.id}`}>상태</InputLabel>
            <Select label="상태" labelId={`safety-status-${report.id}`} value={status} onChange={(event) => setStatus(event.target.value)}>
              {Object.entries(statusLabels).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField fullWidth inputProps={{ maxLength: 1000 }} label="관리자 메모" multiline onChange={(event) => setAdminNote(event.target.value)} size="small" value={adminNote} />
          <Button disabled={busy || (status === report.status && adminNote === (report.adminNote || ''))} onClick={save} variant="contained">저장</Button>
        </Stack>
      </Stack>
    </Paper>
  );
}

export default function ContentSafetyManagement() {
  const [filter, setFilter] = useState('PENDING');
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await adminApi.getSafetyReports(filter || undefined);
      setReports(response.data || []);
    } catch (e) {
      setError(e.message || '신고 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { void load(); }, [load]);

  return (
    <Stack spacing={2}>
      <Typography variant="h6">신고 관리</Typography>
      <Alert severity="info">대상 콘텐츠와 회원 이력을 확인한 뒤 기존 회원·리뷰 관리에서 필요한 조치를 먼저 실행하고, 그 결과를 상태와 메모에 남겨주세요.</Alert>
      <Box sx={{ maxWidth: 220 }}>
        <FormControl fullWidth size="small">
          <InputLabel id="safety-filter">상태 필터</InputLabel>
          <Select label="상태 필터" labelId="safety-filter" value={filter} onChange={(event) => setFilter(event.target.value)}>
            <MenuItem value="">전체</MenuItem>
            {Object.entries(statusLabels).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>
      {error ? <Alert severity="error" action={<Button color="inherit" onClick={load}>재시도</Button>}>{error}</Alert> : null}
      {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress /></Box> : null}
      {!loading && !error && reports.length === 0 ? <Alert severity="success">해당 상태의 신고가 없습니다.</Alert> : null}
      {!loading ? reports.map((report) => <ReportCard key={report.id} onSaved={load} report={report} />) : null}
    </Stack>
  );
}
